import React, { createContext, useState, useEffect, useContext } from 'react';
import { Auth } from 'aws-amplify';

// Create the authentication context
const AuthContext = createContext(null);

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is authenticated on component mount
  useEffect(() => {
    checkAuthState();
  }, []);

  // Check the current authentication state
  const checkAuthState = async () => {
    try {
      setLoading(true);
      const currentUser = await Auth.currentAuthenticatedUser();
      setUser(currentUser);
      setError(null);
    } catch (err) {
      setUser(null);
      if (err !== 'The user is not authenticated') {
        setError(err.message || 'An error occurred during authentication');
      }
    } finally {
      setLoading(false);
    }
  };

  // Sign up a new user
  const signUp = async (email, password, name) => {
    try {
      setLoading(true);
      const { user } = await Auth.signUp({
        username: email,
        password,
        attributes: {
          email,
          name
        }
      });
      return user;
    } catch (err) {
      setError(err.message || 'An error occurred during sign up');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Confirm sign up with verification code
  const confirmSignUp = async (email, code) => {
    try {
      setLoading(true);
      await Auth.confirmSignUp(email, code);
      setError(null);
    } catch (err) {
      setError(err.message || 'An error occurred during confirmation');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Resend confirmation code
  const resendConfirmationCode = async (email) => {
    try {
      setLoading(true);
      await Auth.resendSignUp(email);
      setError(null);
    } catch (err) {
      setError(err.message || 'An error occurred while resending code');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Sign in a user
  const signIn = async (email, password) => {
    try {
      setLoading(true);
      const user = await Auth.signIn(email, password);
      setUser(user);
      setError(null);
      return user;
    } catch (err) {
      setError(err.message || 'An error occurred during sign in');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Sign out the current user
  const signOut = async () => {
    try {
      setLoading(true);
      await Auth.signOut();
      setUser(null);
      setError(null);
    } catch (err) {
      setError(err.message || 'An error occurred during sign out');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Forgot password
  const forgotPassword = async (email) => {
    try {
      setLoading(true);
      await Auth.forgotPassword(email);
      setError(null);
    } catch (err) {
      setError(err.message || 'An error occurred during password reset');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Submit new password with confirmation code
  const forgotPasswordSubmit = async (email, code, newPassword) => {
    try {
      setLoading(true);
      await Auth.forgotPasswordSubmit(email, code, newPassword);
      setError(null);
    } catch (err) {
      setError(err.message || 'An error occurred during password reset');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get the current session
  const getSession = async () => {
    try {
      const session = await Auth.currentSession();
      return session;
    } catch (err) {
      throw err;
    }
  };

  // Get JWT token for API calls
  const getJwtToken = async () => {
    try {
      const session = await Auth.currentSession();
      return session.getIdToken().getJwtToken();
    } catch (err) {
      throw err;
    }
  };

  // Context value
  const value = {
    user,
    loading,
    error,
    signUp,
    confirmSignUp,
    resendConfirmationCode,
    signIn,
    signOut,
    forgotPassword,
    forgotPasswordSubmit,
    getSession,
    getJwtToken,
    checkAuthState
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
